import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, PencilSimple } from '@phosphor-icons/react';
import { REVIEW_NOTE_MIN, type TaskDetail, type Version } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { formatDay } from '../../lib/project-display';
import { formatDateTime } from '../../lib/task-display';
import { usePublishVersion, useResolveFix } from '../../lib/tasks-api';
import { describeChange } from '../../lib/text-diff';
import { versionBadge, versionStateLine, versionSubtitle } from '../../lib/version-display';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Card } from '../ui/card';
import { Dialog } from '../ui/dialog';
import { TextArea } from '../ui/text-area';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';
import { pillClass } from '../ui/toolbar-controls';
import { BriefAssets, BriefPreview } from './brief-preview';
import { ChangedChip, DeliverablePreview } from './deliverable-preview';
import { FixVersionDrawer } from './fix-version-drawer';
import { RequestChangesDialog } from './request-changes-dialog';

const BRIEF = 'brief';

/** "v2": dark when it is the one in play, outlined once it is history (Figma "Version badge" cur/past). */
function Badge({ label, current }: { label: string; current: boolean }) {
  return (
    <span
      className={cx(
        'rounded-(--mbm-radius-xs) px-(--mbm-space-2) py-(--mbm-space-0-5) text-label-small',
        current ? 'bg-action-primary text-text-on-dark' : 'border border-border-default text-text-secondary',
      )}
    >
      {label}
    </span>
  );
}

function VersionRow({
  badge,
  current,
  selected,
  title,
  line,
  onSelect,
}: {
  badge: string;
  current: boolean;
  selected: boolean;
  title: string;
  line: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
      className={cx(
        'flex w-full items-center gap-(--mbm-space-2-5) rounded-(--mbm-radius-md) p-(--mbm-space-2-5) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        selected && 'bg-surface-card shadow-(--mbm-shadow-sm)',
      )}
    >
      <Badge label={badge} current={current} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-label-medium text-text-primary">{title}</span>
        <span className="truncate text-body-xsmall text-text-secondary">{line}</span>
      </span>
    </button>
  );
}

/** What the author wrote for the lead, under the version ("Aisha's note"), or why the lead sent it back. */
function Note({ name, title, children }: { name: string | null; title: string; children: string }) {
  return (
    <div className="flex items-start gap-(--mbm-space-3)">
      <Avatar tone="neutral" size="sm" initials={initialsOf(name)} />
      <div className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
        <span className="text-label-large text-text-primary">{title}</span>
        <p className="whitespace-pre-wrap text-body-medium text-text-secondary">{children}</p>
      </div>
    </div>
  );
}

/**
 * The task page's versions (Figma A05.4): the list on the left, the chosen one on the right. v0 is the brief; v1 and
 * up are what the team handed back. A lead on the task, the account manager or an admin answers a version waiting
 * on QA right here; the same two answers live on the review page (A05.6) with the brief beside the draft.
 */
export function VersionsPanel({
  task,
  versions,
  canEditBrief,
}: {
  task: TaskDetail;
  versions: Version[];
  /** The brief is still a draft the viewer may finish. */
  canEditBrief: boolean;
}) {
  const toast = useToast();
  const publish = usePublishVersion(task.id);
  const [picked, setPicked] = useState<string>();
  const [asking, setAsking] = useState(false);
  const [fixing, setFixing] = useState(false);

  const newest = versions[0];
  const selected = picked === BRIEF ? null : (versions.find((v) => v.id === picked) ?? newest ?? null);
  const first = (name: string | null) => name?.split(' ')[0] ?? 'the author';

  const approve = (version: Version) =>
    publish.mutate(
      { subId: version.id },
      {
        onSuccess: () =>
          toast({
            title: task.requiresClientApproval && task.clientVisible ? 'Sent to the client' : 'Published',
            description: `v${version.versionNumber} is out.`,
            tone: 'success',
          }),
        onError: () => toast({ title: 'Couldn’t publish it', description: 'Please try again.', tone: 'error' }),
      },
    );

  return (
    <Card tone="panel" padding="none" className="grid overflow-hidden md:grid-cols-[minmax(0,13.75rem)_minmax(0,1fr)]">
      <aside className="flex flex-col gap-(--mbm-space-1) bg-surface-field px-(--mbm-space-3) py-(--mbm-space-4)">
        <h2 className="px-(--mbm-space-2-5) pb-(--mbm-space-2) text-caption font-semibold tracking-wide text-text-secondary uppercase">
          Versions
        </h2>
        {versions.map((version, index) => (
          <VersionRow
            key={version.id}
            badge={versionBadge(version.versionNumber)}
            current={index === 0}
            selected={selected?.id === version.id}
            title={`Draft by ${version.authorName ?? 'the team'}`}
            line={versionStateLine(version)}
            onSelect={() => setPicked(version.id)}
          />
        ))}
        <VersionRow
          badge="v0"
          current={versions.length === 0}
          selected={selected === null}
          title={`Brief${task.brief?.authorName ? ` by ${task.brief.authorName}` : ''}`}
          line={`Starting point · ${formatDay(task.createdAt)}`}
          onSelect={() => setPicked(BRIEF)}
        />
        <p className="px-(--mbm-space-2-5) pt-(--mbm-space-3) text-body-xsmall text-text-tertiary">
          Drafts stay internal until a lead or admin sends one to the client.
        </p>
      </aside>

      {selected ? (
        <section className="flex min-w-0 flex-col gap-(--mbm-space-4) p-(--mbm-space-5)">
          <div className="flex items-start justify-between gap-(--mbm-space-3)">
            <div className="flex min-w-0 flex-col gap-(--mbm-space-1)">
              <h2 className="flex items-center gap-(--mbm-space-2) text-heading-h3 text-text-primary">
                Version {selected.versionNumber}
                {selected.editedAt ? <ChangedChip>Edited</ChangedChip> : null}
              </h2>
              <p className="text-body-small text-text-secondary">{versionSubtitle(selected)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-(--mbm-space-3)">
              {task.access.publish && selected.internalStatus === 'pending_lead_review' ? (
                <Link
                  to="/subdomain/admin/workbench/reviews/$taskId"
                  params={{ taskId: task.id }}
                  className="text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  Full review
                </Link>
              ) : null}
              {canFix(task, selected) ? (
                <button type="button" onClick={() => setFixing(true)} className={pillClass('light')}>
                  <Icon icon={PencilSimple} size="sm" />
                  Edit fields
                </button>
              ) : null}
            </div>
          </div>
          <DeliverablePreview version={selected} />
          {selected.notes ? (
            <Note name={selected.authorName} title={`${first(selected.authorName)}’s note`}>
              {selected.notes}
            </Note>
          ) : null}
          {selected.internalStatus === 'changes_requested' && selected.internalReviewNotes ? (
            <Note name={selected.leadReviewerName} title={`${first(selected.leadReviewerName)} asked for changes`}>
              {selected.internalReviewNotes}
            </Note>
          ) : null}
          {selected.pendingFix ? <PendingFixCard task={task} version={selected} /> : null}
          <VersionFooter
            task={task}
            version={selected}
            busy={publish.isPending}
            onApprove={() => approve(selected)}
            onAsk={() => setAsking(true)}
          />
          {asking ? (
            <RequestChangesDialog open onClose={() => setAsking(false)} taskId={task.id} version={selected} />
          ) : null}
          {fixing ? (
            <FixVersionDrawer
              open
              onClose={() => setFixing(false)}
              task={task}
              version={selected}
              inPlace={task.access.publish}
            />
          ) : null}
        </section>
      ) : (
        <section className="flex min-w-0 flex-col gap-(--mbm-space-4) p-(--mbm-space-5)">
          <div className="flex items-start justify-between gap-(--mbm-space-3)">
            <div className="flex flex-col gap-(--mbm-space-1)">
              <h2 className="text-heading-h3 text-text-primary">Version 0</h2>
              <p className="text-body-small text-text-secondary">
                {task.brief?.authorName ? `Submitted by ${task.brief.authorName} · ` : ''}
                {formatDateTime(task.brief?.createdAt ?? task.createdAt)}
                {task.status === 'User Draft' ? ' · still a draft' : ' · the starting point, kept as written'}
              </p>
            </div>
            {canEditBrief ? (
              <Link
                to="/subdomain/admin/tasks/$taskId/edit"
                params={{ taskId: task.id }}
                className={pillClass('light')}
              >
                <Icon icon={PencilSimple} size="sm" />
                Edit fields
              </Link>
            ) : null}
          </div>
          {task.brief ? (
            <>
              <BriefAssets assets={task.brief.assets} />
              <BriefPreview details={task.brief.details} />
            </>
          ) : (
            <p className="text-body-medium text-text-secondary">This task has no brief on file.</p>
          )}
        </section>
      )}
    </Card>
  );
}

/**
 * Whether the viewer can change this version now (Figma "Edit fields"): it has been sent to QA or is live, and they are
 * either a lead and up (edit in place) or someone on it who may propose a fix, with nobody else's proposal in the way.
 */
function canFix(task: TaskDetail, version: Version): boolean {
  if (task.taskType === 'custom' || task.status === 'Cancelled') return false;
  if (version.internalStatus !== 'pending_lead_review' && !version.isPublished) return false;
  if (task.access.publish) return true;
  // Someone else's proposal in the way is for the server to say (409): the page can't tell whose is whose.
  return task.access.work;
}

/** A specialist's proposed fix: what it would change and why. A lead applies it (silently) or declines it. */
function PendingFixCard({ task, version }: { task: TaskDetail; version: Version }) {
  const toast = useToast();
  const resolve = useResolveFix(task.id);
  const [declining, setDeclining] = useState(false);
  const [notes, setNotes] = useState('');
  const fix = version.pendingFix!;
  const before = 'caption' in version.details ? (version.details.caption ?? '') : '';
  const after = 'caption' in fix.proposed ? (fix.proposed.caption ?? '') : '';
  const diff = fix.fields.includes('caption') ? describeChange(before, after) : null;
  const failed = () => toast({ title: 'Couldn’t save that', description: 'Please try again.', tone: 'error' });

  return (
    <div className="flex flex-col gap-(--mbm-space-3) rounded-(--mbm-radius-md) border border-border-default p-(--mbm-space-4)">
      <div className="flex flex-col gap-(--mbm-space-0-5)">
        <span className="text-label-large text-text-primary">
          Draft fix by {fix.authorName ?? 'a specialist'} · {fix.fields.join(', ')}
        </span>
        <span className="text-body-small text-text-secondary">{fix.reason}</span>
        {diff ? <span className="text-body-xsmall text-feedback-success-text">{diff}</span> : null}
        <span className="text-body-xsmall text-text-tertiary">
          The client keeps seeing this version as it is until a lead applies it.
        </span>
      </div>
      {task.access.publish ? (
        <div className="flex items-center gap-(--mbm-space-2)">
          <button type="button" onClick={() => setDeclining(true)} className={pillClass('light')}>
            Decline
          </button>
          <button
            type="button"
            disabled={resolve.isPending}
            onClick={() =>
              resolve.mutate(
                { subId: version.id, approve: true },
                {
                  onSuccess: () =>
                    toast({
                      title: `Applied to v${version.versionNumber}`,
                      description: 'The client wasn’t told.',
                      tone: 'success',
                    }),
                  onError: failed,
                },
              )
            }
            className={pillClass('dark')}
          >
            Apply fix
          </button>
        </div>
      ) : null}
      {declining ? (
        <Dialog
          open
          density="form"
          onClose={() => setDeclining(false)}
          title="Decline this fix"
          description="Tell them why. Only staff see it."
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeclining(false)}>
                Cancel
              </Button>
              <Button
                loading={resolve.isPending}
                disabled={notes.trim().length < REVIEW_NOTE_MIN}
                onClick={() =>
                  resolve.mutate(
                    { subId: version.id, approve: false, notes: notes.trim() },
                    { onSuccess: () => setDeclining(false), onError: failed },
                  )
                }
              >
                Decline fix
              </Button>
            </>
          }
        >
          <TextArea
            label="Why"
            variant="filled"
            density="compact"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`At least ${REVIEW_NOTE_MIN} characters`}
          />
        </Dialog>
      ) : null}
    </div>
  );
}

/** Under a version: the two answers a lead gives, or why there is nothing to answer. */
function VersionFooter({
  task,
  version,
  busy,
  onApprove,
  onAsk,
}: {
  task: TaskDetail;
  version: Version;
  busy: boolean;
  onApprove: () => void;
  onAsk: () => void;
}) {
  const lead = task.team.find((m) => m.staffRole === 'lead_specialist')?.name;
  const waitingOnQa = version.internalStatus === 'pending_lead_review';
  const waitingOnClient = version.isPublished && version.clientStatus === 'pending_review';
  if (!waitingOnQa && !waitingOnClient) return null;

  return (
    <div className="flex items-center justify-between gap-(--mbm-space-3) border-t border-border-line pt-(--mbm-space-4)">
      {waitingOnClient ? (
        <p className="text-body-small text-text-tertiary">Only the client can approve now</p>
      ) : task.access.publish ? (
        <>
          <p className="min-w-0 flex-1 text-body-small text-text-tertiary">
            {lead ? `${lead} (lead) or an admin can approve` : 'A lead on this task or an admin can approve'}
          </p>
          <div className="flex shrink-0 items-center gap-(--mbm-space-2)">
            <button type="button" onClick={onAsk} className={pillClass('light', 'lg')}>
              Request changes
            </button>
            <button type="button" disabled={busy} onClick={onApprove} className={pillClass('dark', 'lg')}>
              <Icon icon={Check} size="sm" />
              Approve & send to client
            </button>
          </div>
        </>
      ) : (
        <p className="text-body-small text-text-tertiary">
          {lead ? `${lead} (lead) or an admin can approve` : 'A lead on this task or an admin can approve'}
        </p>
      )}
    </div>
  );
}
